import { COLORS, FONTS, GRADIENTS } from '../styles/tokens';
import { useTheme } from '../hooks/useTheme';

type OverlayView = 'waiting' | 'success';

interface MfaSetupOverlayProps {
  view: OverlayView;
  methodLabel: string;
  onCancel: () => void;
}

/** Auto-dismiss delay (ms) after successful setup */
const SUCCESS_DISPLAY_MS = 2000;

/**
 * Overlay shown inside SecurityModal while MFA setup happens in a popup window.
 * Shows a spinner + instructions in "waiting" state, or a checkmark in "success" state.
 */
export function MfaSetupOverlay({ view, methodLabel, onCancel }: MfaSetupOverlayProps): React.JSX.Element {
  const { isDark } = useTheme();
  const colors = isDark ? COLORS.dark : COLORS.light;

  if (view === 'success') {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        padding: '48px 24px',
      }}>
        {/* Checkmark circle */}
        <div style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: isDark ? GRADIENTS.buttonDark : GRADIENTS.buttonLight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <p style={{
          fontFamily: FONTS.heading,
          fontSize: '12px',
          letterSpacing: '0.15em',
          color: colors.text,
          margin: 0,
          textAlign: 'center',
        }}>
          SETUP COMPLETE
        </p>
        <p style={{
          fontFamily: FONTS.body,
          fontSize: '12px',
          color: colors.textSecondary,
          margin: 0,
        }}>
          {methodLabel} has been configured.
        </p>
      </div>
    );
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px',
      padding: '48px 24px',
    }}>
      {/* Spinner */}
      <div style={{
        width: '48px',
        height: '48px',
        border: `3px solid ${colors.border}`,
        borderTopColor: COLORS.accent.blue,
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
      }} />

      <p style={{
        fontFamily: FONTS.heading,
        fontSize: '12px',
        letterSpacing: '0.15em',
        color: colors.text,
        margin: 0,
        textAlign: 'center',
      }}>
        COMPLETE SETUP IN THE SECURITY WINDOW
      </p>

      <p style={{
        fontFamily: FONTS.body,
        fontSize: '12px',
        color: colors.textSecondary,
        margin: 0,
      }}>
        Setting up {methodLabel}
      </p>

      <button
        onClick={onCancel}
        style={{
          marginTop: '8px',
          background: 'none',
          border: `1px solid ${colors.border}`,
          borderRadius: '8px',
          padding: '8px 24px',
          cursor: 'pointer',
          fontFamily: FONTS.heading,
          fontSize: '10px',
          letterSpacing: '0.1em',
          color: colors.textSecondary,
        }}
      >
        CANCEL
      </button>

      {/* Inline keyframe for spinner */}
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

export { SUCCESS_DISPLAY_MS };
